import { Effect, Schema } from "effect";
import * as Errors from "./errors.ts";

// Every frame is one JSON text discriminated on `type`, in the wire's key order.

// A name is what someone typed to ready up, without the spaces around it.
export const NAME_MAX = 16;

const Slot = Schema.Int.check(Schema.isGreaterThanOrEqualTo(0));
const Direction = Schema.Literals(["down", "left", "right"]);

export const Player = Schema.Struct({
  slot: Slot,
  // Empty until they ready up.
  name: Schema.String.check(Schema.isMaxLength(NAME_MAX)),
  ready: Schema.Boolean,
  column: Schema.Int,
  row: Schema.Int,
  swing: Schema.Struct({
    column: Schema.Int,
    row: Schema.Int,
    direction: Direction,
    at: Schema.Finite,
  }),
}).annotate({ identifier: "@beatdig/shared/protocol/Player" });

const Wound = Schema.Struct({
  column: Schema.Int,
  row: Schema.Int,
  health: Schema.Finite.check(Schema.isGreaterThanOrEqualTo(0)),
});

// What the room keeps in its storage; the players are kept on their own sockets.
export const Site = Schema.Struct({
  started: Schema.Boolean,
  ground: Schema.Array(Wound),
}).annotate({ identifier: "@beatdig/shared/protocol/Site" });
export type Site = typeof Site.Type;

export const ClientMessage = Schema.Union([
  Schema.Struct({ type: Schema.Literal("ping"), t0: Schema.Finite }),
  Schema.Struct({
    type: Schema.Literal("ready"),
    name: Schema.String.check(Schema.isTrimmed(), Schema.isLengthBetween(1, NAME_MAX)),
  }),
  Schema.Struct({ type: Schema.Literal("dig"), direction: Direction }),
]).annotate({ identifier: "@beatdig/shared/protocol/ClientMessage" });
export type ClientMessage = typeof ClientMessage.Type;

// A world is told to each player with `you`, the slot that is theirs.
export const ServerMessage = Schema.Union([
  Schema.Struct({
    type: Schema.Literal("pong"),
    t0: Schema.Finite,
    t1: Schema.Finite,
    t2: Schema.Finite,
  }),
  Schema.Struct({
    type: Schema.Literal("world"),
    you: Slot,
    started: Schema.Boolean,
    players: Schema.Array(Player),
    ground: Schema.Array(Wound),
  }),
]).annotate({ identifier: "@beatdig/shared/protocol/ServerMessage" });
export type ServerMessage = typeof ServerMessage.Type;

const ClientLine = Schema.fromJsonString(Schema.toCodecJson(ClientMessage));
const ServerLine = Schema.fromJsonString(Schema.toCodecJson(ServerMessage));

export const encodeClientMessage: (message: ClientMessage) => string =
  Schema.encodeSync(ClientLine);
export const encodeServerMessage: (message: ServerMessage) => string =
  Schema.encodeSync(ServerLine);

const decodeClientLine = Schema.decodeUnknownEffect(ClientLine);
const decodeServerLine = Schema.decodeUnknownEffect(ServerLine);

// Why a frame fails to decode is the text itself: the error names it, not the schema issue.
export const decodeClientMessage = (
  text: string,
): Effect.Effect<ClientMessage, Errors.BadMessage> =>
  Effect.mapError(decodeClientLine(text), () => Errors.BadMessage.make({ text }));

export const decodeServerMessage = (
  text: string,
): Effect.Effect<ServerMessage, Errors.BadMessage> =>
  Effect.mapError(decodeServerLine(text), () => Errors.BadMessage.make({ text }));
